/**
 * Analytics-consent storage helpers.
 *
 * Pure, dependency-free module: it imports nothing app-specific so it can be
 * shared by both `consent-context.jsx` and `use-sentry.js` without creating a
 * circular import between them. Holds the localStorage key, state enum, and
 * the read/write/clear helpers for the `lodgr.consent.analytics` choice.
 */
export const CONSENT_STORAGE_KEY = 'lodgr.consent.analytics'

// The single "We value your privacy" banner governs both PostHog analytics and
// Sentry Session Replay. We mirror the same choice under a dedicated
// session-replay key so the replay consent is independently inspectable in
// storage (and matches CONSENT_TESTING.md). `CONSENT_STORAGE_KEY` stays the
// canonical value read by the app.
export const SESSION_REPLAY_STORAGE_KEY = 'lodgr.consent.session_replay'

const CONSENT_STORAGE_KEYS = Object.freeze([
    CONSENT_STORAGE_KEY,
    SESSION_REPLAY_STORAGE_KEY,
])

export const CONSENT_STATE = Object.freeze({
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    // eslint-disable-next-line unicorn/no-null -- null is the sentinel for "no choice recorded yet"
    UNKNOWN: null,
})

/**
 * Read the persisted consent value from localStorage. Exported so the boot
 * orchestration in `main.jsx` can synchronously inspect prior consent BEFORE
 * `initPostHog()` runs and pass `optOutByDefault: true` when the user has
 * previously declined. Without this, PostHog briefly initializes in
 * capturing mode and any events fired between `posthog.init()` and the
 * `ConsentProvider.onMount` re-assertion of opt-out would leak.
 */
export const readStoredConsent = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return CONSENT_STATE.UNKNOWN
    }
    try {
        const value = window.localStorage.getItem(CONSENT_STORAGE_KEY)
        if (value === CONSENT_STATE.ACCEPTED) return CONSENT_STATE.ACCEPTED
        if (value === CONSENT_STATE.REJECTED) return CONSENT_STATE.REJECTED
        return CONSENT_STATE.UNKNOWN
    } catch {
        return CONSENT_STATE.UNKNOWN
    }
}

export const writeStoredConsent = (value) => {
    if (typeof window === 'undefined' || !window.localStorage) return
    try {
        for (const key of CONSENT_STORAGE_KEYS) {
            window.localStorage.setItem(key, value)
        }
    } catch {
        // Storage unavailable / quota exceeded — accept the loss; the SDK's
        // own opt-out state still persists across the page session.
    }
}

export const clearStoredConsent = () => {
    if (typeof window === 'undefined' || !window.localStorage) return
    try {
        for (const key of CONSENT_STORAGE_KEYS) {
            window.localStorage.removeItem(key)
        }
    } catch {
        // Storage unavailable — accept the loss.
    }
}
